import React, { useEffect, useMemo, useState } from "react"
import { faChevronLeft, faChevronRight, faClose, faImage } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { IPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface PublicMenuImagePreviewPupopProps {
  image?: string | null
  images?: string[]
  initialIndex?: number
  title: string
  theme?: IPublicMenuTheme | null
  onClose: () => void
}

export const PublicMenuImagePreviewPupop: React.FC<PublicMenuImagePreviewPupopProps> = ({
  image,
  images,
  initialIndex = 0,
  title,
  theme,
  onClose,
}) => {
  const { t } = useTranslation()

  const isLegacyTheme = !theme || isLegacyPublicMenuTheme(theme)

  const [activeIndex, setActiveIndex] = useState(initialIndex)

  const [failedImages, setFailedImages] = useState<string[]>([])

  const availableImages = useMemo(() => {
    const galleryImages = images?.filter((currentImage) => currentImage.trim().length > 0) ?? []

    const fallbackImage = image?.trim()

    const resolvedImages = galleryImages.length > 0 ? galleryImages : fallbackImage ? [fallbackImage] : []

    return resolvedImages.filter(
      (currentImage, index) => resolvedImages.indexOf(currentImage) === index && !failedImages.includes(currentImage),
    )
  }, [failedImages, image, images])

  useEffect(() => {
    if (availableImages.length === 0) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
        return
      }

      if (availableImages.length <= 1) {
        return
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault()
        setActiveIndex((current) => (current - 1 + availableImages.length) % availableImages.length)
      }

      if (event.key === "ArrowRight") {
        event.preventDefault()
        setActiveIndex((current) => (current + 1) % availableImages.length)
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [availableImages.length, onClose])

  if (availableImages.length === 0) {
    return null
  }

  const displayedIndex = Math.min(Math.max(activeIndex, 0), availableImages.length - 1)

  const activeImage = availableImages[displayedIndex]

  const showPreviousImage = () => {
    setActiveIndex((current) => (current - 1 + availableImages.length) % availableImages.length)
  }

  const showNextImage = () => {
    setActiveIndex((current) => (current + 1) % availableImages.length)
  }

  return (
    <ModalPortal>
      <div
        className={defaultStyle.overlay}
        onClick={(event) => event.stopPropagation()}
        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.publicMenuImagePreviewPupop.button.eventEventStopPropagation"
      >
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section
            className={defaultStyle.alert1}
            style={isLegacyTheme || !theme ? undefined : { backgroundColor: theme.surfaceColor, color: theme.textColor }}
          >
            <div
              className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2"
              style={isLegacyTheme || !theme ? undefined : { borderColor: theme.borderColor }}
            >
              <div className="flex min-w-0 items-center gap-2">
                <FontAwesomeIcon
                  icon={faImage}
                  className="shrink-0 text-orange-500"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.primaryColor }}
                />

                <h2
                  className="truncate text-sm font-black uppercase tracking-wide text-gray-900"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.textColor }}
                >
                  {title}
                </h2>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label={t("publicMenu:cart.close")}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white transition hover:bg-red-700"
                style={isLegacyTheme || !theme ? undefined : { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }}
                data-interaction-description-key="interactionDescriptions:publicMenu.pupop.publicMenuImagePreviewPupop.button.onClose"
              >
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>

            <Animate type={AnimationTypes.zoomIn} duration="1.5s">
              <div className="relative flex h-[75dvh] max-h-[75dvh] w-full items-center justify-center overflow-hidden rounded-lg p-2">
                <img
                  src={activeImage.toClientRequest()}
                  alt={
                    availableImages.length > 1
                      ? t("publicMenu:add_meal_gallery.image_alt", { name: title, index: displayedIndex + 1 })
                      : title
                  }
                  onError={() =>
                    availableImages.length > 1 &&
                    setFailedImages((current) => (current.includes(activeImage) ? current : [...current, activeImage]))
                  }
                  className="h-full w-full rounded-lg object-contain"
                />

                {availableImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={showPreviousImage}
                      aria-label={t("publicMenu:add_meal_gallery.previous_image")}
                      className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-black/60 text-white shadow-lg transition hover:scale-105 hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pupop.publicMenuImagePreviewPupop.button.previousImage"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>

                    <button
                      type="button"
                      onClick={showNextImage}
                      aria-label={t("publicMenu:add_meal_gallery.next_image")}
                      className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-black/60 text-white shadow-lg transition hover:scale-105 hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pupop.publicMenuImagePreviewPupop.button.nextImage"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>

                    <div className="absolute bottom-3 left-1/2 flex max-w-[90%] -translate-x-1/2 gap-1.5 overflow-x-auto rounded-full bg-black/60 px-3 py-2">
                      {availableImages.map((currentImage, index) => (
                        <button
                          key={currentImage}
                          type="button"
                          onClick={() => setActiveIndex(index)}
                          aria-label={t("publicMenu:add_meal_gallery.select_image", { index: index + 1 })}
                          className={`h-2.5 shrink-0 rounded-full transition-all ${
                            index === displayedIndex ? "w-6 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"
                          }`}
                          data-interaction-description-key="interactionDescriptions:publicMenu.pupop.publicMenuImagePreviewPupop.button.selectImage"
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </Animate>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
